import type {
  AppointmentPlanningManualAppointmentPayload,
  AppointmentPlanningPreviewResponse,
} from "../../../../../../api/kehrblatt/planning"
import type { AppointmentPlanningCalendarAppointmentChange } from "../_shared/_logic/calendar-contract"
import type { AppointmentPlanningWorkOrderOverride } from "../../../_logic/appointments-contract"
import { formatDateOnly } from "../../../_logic/dates"
import { compactAppointmentPlanningOverride } from "./rowOverrides"
import type { AppointmentPlanningStateCommand } from "./contract"
import {
  appointmentMeetsStartBound,
  type AppointmentPlanningStartBound,
} from "../_shared/_logic/start-bound"

export type ApplyCalendarAppointmentChangeIgnoredReason =
  | "blocked"
  | "missing_preview"
  | "missing_client_row_id"
  | "unknown_row"
  | "before_start"

export type ApplyCalendarAppointmentChangeResult =
  | { kind: "ignored"; reason: ApplyCalendarAppointmentChangeIgnoredReason }
  | {
      kind: "applied"
      previewResponse: AppointmentPlanningPreviewResponse
      workOrderOverridesByRowId: Record<string, AppointmentPlanningWorkOrderOverride>
      commands: AppointmentPlanningStateCommand[]
    }

const manualAppointmentFromCalendarChange = (
  appointment: AppointmentPlanningCalendarAppointmentChange["appointment"],
): AppointmentPlanningManualAppointmentPayload => ({
  start_at: appointment.start_at,
  end_at: appointment.end_at,
  employee_id: appointment.employee_id,
  flex_type: appointment.flex_type === "SOFT" ? "SOFT" : "HARD",
  timeframe_start_at: appointment.timeframe_start_at ?? appointment.start_at,
  timeframe_end_at: appointment.timeframe_end_at ?? appointment.end_at,
})

const nextOverrideForCalendarChange = ({
  current,
  detail,
}: {
  current: AppointmentPlanningWorkOrderOverride
  detail: AppointmentPlanningCalendarAppointmentChange
}): AppointmentPlanningWorkOrderOverride | null => {
  const manualAppointment = manualAppointmentFromCalendarChange(detail.appointment)
  const isFlexAppointment = manualAppointment.flex_type === "SOFT"

  return compactAppointmentPlanningOverride({
    ...current,
    planning_strategy: "MANUAL",
    manual_appointment_type: isFlexAppointment ? "FLEX" : "HARD",
    appointment_employee_id: detail.appointment.employee_id,
    manual_appointment: manualAppointment,
    manual_flex_date: isFlexAppointment
      ? formatDateOnly(manualAppointment.timeframe_start_at ?? manualAppointment.start_at)
      : null,
    manual_flex_slot_id: null,
    manual_flex_appointment: isFlexAppointment ? manualAppointment : null,
  })
}

export const applyCalendarAppointmentChange = ({
  startBound,
  blocked,
  previewResponse,
  workOrderOverridesByRowId,
  detail,
}: {
  startBound: AppointmentPlanningStartBound
  blocked: boolean
  previewResponse: AppointmentPlanningPreviewResponse | null
  workOrderOverridesByRowId: Record<string, AppointmentPlanningWorkOrderOverride>
  detail: AppointmentPlanningCalendarAppointmentChange
}): ApplyCalendarAppointmentChangeResult => {
  if (blocked) return { kind: "ignored", reason: "blocked" }

  if (!appointmentMeetsStartBound(detail.appointment, startBound)) {
    return { kind: "ignored", reason: "before_start" }
  }

  if (!previewResponse) return { kind: "ignored", reason: "missing_preview" }

  const clientRowId = String(detail.clientRowId ?? "").trim()

  if (!clientRowId) return { kind: "ignored", reason: "missing_client_row_id" }

  if (!previewResponse.rows.some((row) => row.client_row_id === clientRowId)) {
    return { kind: "ignored", reason: "unknown_row" }
  }

  const nextOverrides = { ...workOrderOverridesByRowId }
  const compacted = nextOverrideForCalendarChange({
    current: workOrderOverridesByRowId[clientRowId] ?? {},
    detail: { ...detail, clientRowId },
  })

  if (compacted) nextOverrides[clientRowId] = compacted
  else delete nextOverrides[clientRowId]

  const commands: AppointmentPlanningStateCommand[] = [
    { kind: "mark_row_planning_dirty", rowId: clientRowId },
  ]

  if (detail.appointment.flex_type === "SOFT") {
    commands.push({ kind: "load_manual_flex_slots", rowId: clientRowId })
  }

  return {
    kind: "applied",
    previewResponse,
    workOrderOverridesByRowId: nextOverrides,
    commands,
  }
}
